/**
 * walkHerePlan — pure planning for the right-click "Walk here" flow (ENG-1050):
 * ground point in, kimodo-ready route out. No THREE / DOM / network, so it
 * unit-tests under `node --test` (its dep, bodyMotionPath's planWaypointRoute,
 * is itself THREE-free).
 *
 * WHY THE DISTANCE CAP. genBodyMotionOnWaypoints derives its clip duration from
 * route length via clampRouteDuration, which clamps to [1.5, 15] s
 * (bodyMotionPath.js). At WALK_SPEED that means a route past ~19.5 m stops being
 * a walk and becomes a sprint, and Caner reported generations past ~10 s going
 * unstable (the avatar teleporting around). So a walk-here click further than
 * MAX_WALK_M pulls the goal IN along its own planned route and reports
 * `clamped: true` — the menu then reads "Walk here (nearest reachable)". Walking
 * part of the way and saying so beats generating a clip in the broken zone.
 *
 * The lower bound exists for the same reason at the other end: the duration
 * clamp floors at 1.5 s, so a 10 cm route generates 1.5 s of locomotion that has
 * nowhere to go and foot-skates in place.
 */
import { DEFAULT_SMOOTH, distXZ, pathLengthXZ, planWaypointRoute } from './bodyMotionPath.js'

/** m/s. Fixed, not exposed in the menu (Hick's Law) — matches
 *  genBodyMotionOnWaypoints's own default so the gait is the one it was tuned for. */
export const WALK_SPEED = 1.3

/** Longest route one click may request. ~10 s at the default walk pace, under
 *  the 15 s duration clamp and under the instability Caner flagged; a faster
 *  gait (the Run preset, 3 m/s) covers the same 13 m in ~4.3 s, so the cap is
 *  set by the distance a single click should commit to, not by the clip length
 *  alone. */
export const MAX_WALK_M = 13

/** Shortest route worth generating (see the header). Below this the click is
 *  treated as "already here" rather than producing a skating clip. */
export const MIN_WALK_M = 0.5

/**
 * Truncate a polyline to at most `maxM` of XZ arc length, interpolating the
 * final point so the cap is exact rather than snapping to a vertex.
 *
 * @param {number[][]} path  [[x,y,z], …]
 * @param {number} maxM
 * @returns {{path:number[][], clamped:boolean, length:number}}
 */
export function clampPathToDistance(path, maxM) {
  if (!Array.isArray(path) || path.length < 2) {
    return { path: path ?? [], clamped: false, length: 0 }
  }
  const total = pathLengthXZ(path)
  if (!(maxM > 0) || total <= maxM) return { path, clamped: false, length: total }

  const out = [path[0]]
  let acc = 0
  for (let i = 1; i < path.length; i++) {
    const seg = distXZ(path[i - 1], path[i])
    if (acc + seg < maxM) {
      out.push(path[i])
      acc += seg
      continue
    }
    // The cap falls inside this segment — interpolate the endpoint. A zero-length
    // segment can't be interpolated (t would divide by 0); it also can't be the
    // one that crosses the cap unless maxM === acc, so take its end verbatim.
    const t = seg > 0 ? (maxM - acc) / seg : 1
    const a = path[i - 1], b = path[i]
    out.push([
      a[0] + (b[0] - a[0]) * t,
      a[1] + (b[1] - a[1]) * t,
      a[2] + (b[2] - a[2]) * t,
    ])
    break
  }
  return { path: out, clamped: true, length: pathLengthXZ(out) }
}

/**
 * Plan a walk from the avatar's current position to a clicked ground point.
 *
 * Throws — loudly and with the underlying message — when no path exists
 * (`planWaypointRoute`) or when the goal is effectively where the avatar already
 * stands. A point OFF the navmesh is not an error: the route reaches the nearest
 * walkable point and covers the rest in a straight line. The caller turns each
 * of those into a DISABLED menu entry carrying the reason; none of them is
 * papered over with a straight line (that is an explicit user mode in the
 * wizard, never a fallback here).
 *
 * @param {object} o
 * @param {object} o.navmesh          a NavMesh instance (required)
 * @param {number[]} o.from           avatar world position [x,y,z]
 * @param {number[]} o.to             clicked ground point [x,y,z]
 * @param {number} [o.maxDistance=MAX_WALK_M]
 * @param {number} [o.minDistance=MIN_WALK_M]
 * @param {'none'|'curve'} [o.smooth='none']  route shape; returned
 *   on the plan so the caller sends the SAME shape it previewed. It also changes
 *   the distance the cap is measured against: a diagonal staircase is ~41%
 *   longer than the line it stands for, so smoothing lets a click reach farther
 *   before it clamps, which is the honest distance rather than a looser cap.
 * @returns {{waypoints:number[][], route:object, distance:number, clamped:boolean, destination:number[], smooth:string}}
 */
export function planWalkHere({ navmesh, from, to, maxDistance = MAX_WALK_M, minDistance = MIN_WALK_M, smooth = DEFAULT_SMOOTH }) {
  if (!navmesh) throw new Error('no navmesh loaded for this environment')
  if (!Array.isArray(from) || from.length < 3) throw new Error('avatar position unavailable')
  if (!Array.isArray(to) || to.length < 3) throw new Error('no ground point under the cursor')

  // The clicked point is used AS-IS. planWaypointRoute handles a point off the
  // mesh by routing to the nearest point on it and then walking the last stretch
  // in a straight line, so a click just off the walkable area is a short walk to
  // where you pointed rather than a refusal. This used to reject anything more
  // than a metre off (pickWaypoint), which contradicted the wizard, where the
  // same click plans fine.

  const full = planWaypointRoute(navmesh, [from, to], { mode: 'navmesh', smooth })
  if (full.length < minDistance) {
    throw new Error(`already here (${full.length.toFixed(2)} m)`)
  }

  const { path, clamped, length } = clampPathToDistance(full.path, maxDistance)
  // Stamp the WORLD floor Y on every point. Navmesh nodes carry the bake space's
  // floor height (a real one measured: -6.2 while the avatar stands at 0), so a
  // path taken straight off the graph hands its consumers a y that is metres from
  // the floor. kimodo's root2d constraint reads XZ only, so this is inert there —
  // which is exactly why it would rot silently until something else read it.
  const y = from[1]
  const worldPath = path.map(pt => [pt[0], y, pt[2]])
  const destination = worldPath[worldPath.length - 1]
  // A clamped route is still ONE navmesh polyline, so rebuild the single-segment
  // shape planWaypointRoute returns rather than re-planning to the pulled-in
  // point (which could route differently and land somewhere else again).
  const route = clamped
    ? { mode: 'navmesh', smooth, path: worldPath, segments: [{ from, to: destination, path: worldPath, length }], length }
    : { ...full, path: worldPath, segments: full.segments.map(s => ({ ...s, path: s.path.map(pt => [pt[0], y, pt[2]]) })) }

  return {
    waypoints: [from, destination],
    route,
    distance: length,
    clamped,
    destination,
    smooth,
  }
}
